// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { useEffect, useRef } from 'react';
import { api } from '../lib/api';
import { withAuthRetry } from '../lib/authRetry';
import { logger } from '../lib/logger';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';

// Helper to decode JWT and get expiration time
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
  } catch {
    return null;
  }
}

export function useTokenRefresh(): void {
  const { clearAuth, setAuth } = useAuthStore();
  // Mutex: tracks an in-flight proactive refresh so the 401 handler can wait
  // instead of racing with it and immediately calling clearAuth().
  const isRefreshingRef = useRef<boolean>(false);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);

  // Wire up 401 handler to attempt token refresh before clearing auth
  useEffect(() => {
    api.setUnauthorizedHandler(async () => {
      logger.warn('[App] 401 Unauthorized - attempting token refresh');

      // If a proactive refresh is already in flight, wait for it instead of
      // racing with it. The proactive refresh will update auth state on success,
      // so after it resolves we check whether we're still authenticated.
      if (isRefreshingRef.current && refreshPromiseRef.current) {
        logger.debug('[App] Proactive refresh in progress — waiting for it to complete');
        try {
          await refreshPromiseRef.current;
          // If the proactive refresh succeeded, auth state was updated — don't log out.
          if (useAuthStore.getState().accessToken) {
            logger.info('[App] Proactive refresh completed — 401 resolved');
            return;
          }
        } catch {
          // Proactive refresh failed; fall through to clear auth below.
        }
      }

      const { refreshToken, user } = useAuthStore.getState();

      if (refreshToken && user) {
        try {
          const response = await withAuthRetry(
            () => api.refreshToken({ refresh_token: refreshToken }),
            'unauthorized_token_refresh'
          );
          logger.info('[App] Token refreshed successfully');
          // Update auth state with new tokens
          setAuth(user, response.access_token, response.refresh_token);
          return; // Don't clear auth - refresh succeeded
        } catch (refreshError) {
          logger.error('[App] Token refresh failed after retries', {
            error: refreshError,
          });
        }
      }

      // Refresh failed or no refresh token - clear auth
      clearAuth();
      const { setSessionError } = useGameStore.getState();
      setSessionError('Your session has expired. Please log in again to continue.');
    });
  }, [clearAuth, setAuth]);

  // Monitor token expiration and refresh proactively before expiry
  useEffect(() => {
    const accessToken = useAuthStore.getState().accessToken;
    const authenticated = useAuthStore.getState().user !== null;

    if (!authenticated || !accessToken) return;

    const expiryTime = getTokenExpiry(accessToken);
    if (!expiryTime) return;

    const now = Date.now();
    const timeUntilExpiry = expiryTime - now;

    // If already expired, try to refresh immediately
    if (timeUntilExpiry <= 0) {
      logger.warn('[App] Token already expired - attempting refresh');
      const { refreshToken, user } = useAuthStore.getState();
      if (refreshToken && user) {
        withAuthRetry(
          () => api.refreshToken({ refresh_token: refreshToken }),
          'expired_token_refresh'
        )
          .then(response => {
            logger.info('[App] Token refreshed after expiry');
            setAuth(user, response.access_token, response.refresh_token);
          })
          .catch(() => {
            logger.error('[App] Token refresh failed after expiry');
            clearAuth();
            const { setSessionError } = useGameStore.getState();
            setSessionError('Your session has expired. Please log in again to continue.');
          });
      } else {
        clearAuth();
        const { setSessionError } = useGameStore.getState();
        setSessionError('Your session has expired. Please log in again to continue.');
      }
      return;
    }

    // Refresh 5 minutes before expiry to prevent disruption during gameplay
    const REFRESH_BUFFER_MS = 5 * 60 * 1000; // 5 minutes
    const timeUntilRefresh = Math.max(0, timeUntilExpiry - REFRESH_BUFFER_MS);

    logger.debug('[App] Token expiry schedule', {
      expiresInMinutes: Math.round(timeUntilExpiry / 1000 / 60),
      refreshInMinutes: Math.round(timeUntilRefresh / 1000 / 60),
    });

    // Set timer to refresh token before it expires
    const refreshTimer = setTimeout(() => {
      if (isRefreshingRef.current) {
        logger.debug('[App] Proactive refresh skipped — refresh already in progress');
        return;
      }
      logger.info('[App] Proactively refreshing token before expiry');
      const { refreshToken, user } = useAuthStore.getState();
      if (refreshToken && user) {
        isRefreshingRef.current = true;
        // Store the promise so the 401 handler can await it instead of racing
        refreshPromiseRef.current = withAuthRetry(
          () => api.refreshToken({ refresh_token: refreshToken }),
          'proactive_token_refresh'
        )
          .then(response => {
            logger.info('[App] Token refreshed proactively');
            setAuth(user, response.access_token, response.refresh_token);
          })
          .catch(error => {
            logger.error('[App] Proactive token refresh failed after retries', { error });
            // Don't immediately clear auth - let the expiry timer handle it
          })
          .finally(() => {
            isRefreshingRef.current = false;
            refreshPromiseRef.current = null;
          });
      }
    }, timeUntilRefresh);

    // Also set a fallback timer at actual expiry
    const expiryTimer = setTimeout(() => {
      if (isRefreshingRef.current) {
        logger.debug('[App] Expiry handler skipped — refresh already in progress');
        return;
      }

      // Check if token was refreshed (expiry would be different now)
      const currentToken = useAuthStore.getState().accessToken;
      if (currentToken) {
        const currentExpiry = getTokenExpiry(currentToken);
        if (currentExpiry && currentExpiry > Date.now()) {
          logger.debug('[App] Token was already refreshed, skipping expiry handler');
          return;
        }
      }

      logger.warn('[App] Token expired - attempting final refresh');
      const { refreshToken, user } = useAuthStore.getState();
      if (refreshToken && user) {
        isRefreshingRef.current = true;
        withAuthRetry(
          () => api.refreshToken({ refresh_token: refreshToken }),
          'final_token_refresh'
        )
          .then(response => {
            logger.info('[App] Token refreshed at expiry');
            setAuth(user, response.access_token, response.refresh_token);
          })
          .catch(() => {
            logger.error('[App] Final token refresh failed after retries');
            clearAuth();
            const { setSessionError } = useGameStore.getState();
            setSessionError('Your session has expired. Please log in again to continue.');
          })
          .finally(() => {
            isRefreshingRef.current = false;
          });
      } else {
        clearAuth();
        const { setSessionError } = useGameStore.getState();
        setSessionError('Your session has expired. Please log in again to continue.');
      }
    }, timeUntilExpiry);

    return () => {
      clearTimeout(refreshTimer);
      clearTimeout(expiryTimer);
    };
  }, [setAuth, clearAuth]);
}
